import React, { useState, useEffect } from 'react';
import { useSelector } from "react-redux";
import Select from 'react-select';
import * as XLSX from 'xlsx';
import { FaCampground, FaUsers, FaChalkboardTeacher, FaMapMarkedAlt } from 'react-icons/fa';

const DFLProjectSummary = () => {
  // Get project details from Redux
  const pid = useSelector((state) => state.user.projectId);
  const projectName = useSelector((state) => state.user.projectName);

  // State for data
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Summary data
  const [totalCamps, setTotalCamps] = useState(0);
  const [completedCamps, setCompletedCamps] = useState(0);
  const [ongoingCamps, setOngoingCamps] = useState(0);
  const [upcomingCamps, setUpcomingCamps] = useState(0);
  const [mneCamps, setMneCamps] = useState(0);
  const [deletedCamps, setDeletedCamps] = useState(0);
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [totalTrainers, setTotalTrainers] = useState(0);
  const [totalState, setTotalState] = useState(0);
  
  // Case status counts
  const [openCount, setOpenCount] = useState(0);
  const [documentSubmitCount, setDocumentSubmitCount] = useState(0);
  const [brCount, setBrCount] = useState(0);
  const [caseRejectedCount, setCaseRejectedCount] = useState(0);
  const [proofRejectedCount, setProofRejectedCount] = useState(0);
  const [dataCompletedCount, setDataCompletedCount] = useState(0);

  // Gender data
  const [maleCitizen, setMaleCitizen] = useState(0);
  const [femaleCitizen, setFemaleCitizen] = useState(0);
  const [otherCitizen, setOtherCitizen] = useState(0);

  // Filter states
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedCampType, setSelectedCampType] = useState(null);
  const [selectedCampStatus, setSelectedCampStatus] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Filter options
  const [stateOptions, setStateOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [campTypeOptions, setCampTypeOptions] = useState([]);
  const [campStatusOptions, setCampStatusOptions] = useState([]);
  
  // Applied filters
  const [appliedFilters, setAppliedFilters] = useState({
    selectedState: null,
    selectedDistrict: null,
    selectedCampType: null,
    selectedCampStatus: null,
    startDate: '',
    endDate: ''
  });

  // Info popup state
  const [infoPopup, setInfoPopup] = useState({ show: false, title: '', description: '', example: '' });

  // Definitions
  const definitions = {
    "DFL Camps High Level Summary": {
      title: "DFL Camps High Level Summary",
      description: "This dashboard provides a comprehensive overview of DFL camps including camp distribution, application statuses, and gender distribution across different regions.",
      example: "• Total Camps: All camps conducted\n• Completed Camps: Camps marked as complete\n• Applications Opened: Cases started\n• Applications with Benefit Received: Cases where benefits were received"
    },
    "Date Range": {
      title: "Date Range",
      description: "These dates will be used to report on the work done between these dates. Both the start date and end date are inclusive in the query.",
      example: "If you select 1/Jan/2026 - 31/Mar/2026, the data will include work done on 1st January AND 31st March as well."
    },
    "State": {
      title: "State",
      description: "Filter data by state to analyze camp performance in specific regions.",
      example: "Example: Maharashtra, Karnataka, Delhi, Tamil Nadu, etc."
    },
    "District": {
      title: "District",
      description: "Filter data by district to analyze camp performance at the district level.",
      example: "Example: Mumbai, Bangalore, Delhi, Chennai, etc."
    },
    "Camp Type": {
      title: "Camp Type",
      description: "Filter data by camp type to analyze institutional vs non-institutional camps.",
      example: "Example: Institutional, Non-Institutional"
    },
    "Camp Status": {
      title: "Camp Status",
      description: "Filter data by camp status to analyze camps at different stages.",
      example: "Example: Completed, Ongoing, Upcoming, Deleted"
    }
  };

  const InfoButton = ({ title }) => {
    const definition = definitions[title];
    if (!definition) return null;

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setInfoPopup({
            show: true,
            title: definition.title,
            description: definition.description,
            example: definition.example || ''
          });
        }}
        className="group relative ml-1.5 inline-flex items-center justify-center w-4 h-4 flex-shrink-0 rounded-full text-gray-400 transition-colors duration-300 ease-out hover:text-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-indigo-400"
        aria-label={`Info about ${title}`}
      >
        <span className="absolute inset-0 -m-2 rounded-full bg-white/0 ring-1 ring-transparent transition-all duration-300 ease-out group-hover:bg-white/70 group-hover:backdrop-blur-md group-hover:ring-black/5 group-hover:shadow-[0_4px_14px_rgba(99,102,241,0.18)]" />
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="relative w-3.5 h-3.5 transition-transform duration-300 ease-out group-hover:scale-110"
        >
          <circle cx="12" cy="12" r="9.5" />
          <path d="M12 16v-4.5" />
          <path d="M12 7.8h.01" />
        </svg>
      </button>
    );
  };

  const InfoPopup = () => {
    if (!infoPopup.show) return null;
    
    return (
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]"
        onClick={closeInfoPopup}
      >
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-0 overflow-hidden animate-scaleIn"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 18a9 9 0 100-18 9 9 0 000 18z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white">{infoPopup.title}</h3>
            </div>
            <button
              onClick={closeInfoPopup}
              className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="px-6 py-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="mt-0.5">
                <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-700 leading-relaxed">{infoPopup.description}</p>
            </div>

            {infoPopup.example && (
              <div className="mt-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">📊</span>
                  <p className="text-sm font-semibold text-gray-800">Example</p>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-line font-mono bg-white/50 p-3 rounded-lg border border-blue-100">
                  {infoPopup.example}
                </p>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeInfoPopup}
                className="px-5 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const closeInfoPopup = () => {
    setInfoPopup({ show: false, title: '', description: '', example: '' });
  };

  // Get query params
  const getQueryParams = () => {
    const params = new URLSearchParams();
    if (pid) params.append("pid", pid);
    if (projectName) params.append("ProjectName", projectName);
    if (appliedFilters.selectedState) params.append("State", appliedFilters.selectedState);
    if (appliedFilters.selectedDistrict) params.append("District", appliedFilters.selectedDistrict);
    if (appliedFilters.selectedCampType) params.append("CampInstType", appliedFilters.selectedCampType);
    if (appliedFilters.selectedCampStatus) params.append("CampStatus", appliedFilters.selectedCampStatus);
    if (appliedFilters.startDate) params.append("start_date", appliedFilters.startDate);
    if (appliedFilters.endDate) params.append("end_date", appliedFilters.endDate);
    return params.toString();
  };

  // Fetch filter options
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const params = getQueryParams();
        
        const stateRes = await fetch(`https://api-dfl-insights.haqdarshak.com/api/unique-state/?${params}`);
        const stateData = await stateRes.json();
        setStateOptions(stateData.State || []);

        const districtRes = await fetch(`https://api-dfl-insights.haqdarshak.com/api/unique-district/?${params}`);
        const districtData = await districtRes.json();
        setDistrictOptions(districtData.District || []);

        const campTypeRes = await fetch(`https://api-dfl-insights.haqdarshak.com/api/unique-inst/?${params}`);
        const campTypeData = await campTypeRes.json();
        setCampTypeOptions(campTypeData.CampInstType || []);

        const campStatusRes = await fetch(`https://api-dfl-insights.haqdarshak.com/api/unique-camp-status/?${params}`);
        const campStatusData = await campStatusRes.json();
        setCampStatusOptions(campStatusData.CampMarkedAsComplete || []);
      } catch (error) {
        console.error("Error fetching filter options:", error);
      }
    };

    if (pid) {
      fetchFilterOptions();
    }
  }, [pid, projectName]);

  // Fetch all data
  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        const params = getQueryParams();

        const totalCampsRes = await fetch(`https://api-dfl-insights.haqdarshak.com/api/total-camps/?${params}`);
        const totalCampsData = await totalCampsRes.json();
        setTotalCamps(totalCampsData.TotalCamps || 0);

        const completedRes = await fetch(`https://api-dfl-insights.haqdarshak.com/api/completed-camps/?${params}`);
        const completedData = await completedRes.json();
        setCompletedCamps(completedData.CompletedCamps || 0);

        const ongoingRes = await fetch(`https://api-dfl-insights.haqdarshak.com/api/ongoing-camps/?${params}`);
        const ongoingData = await ongoingRes.json();
        setOngoingCamps(ongoingData.OngoingCamps || 0);

        const upcomingRes = await fetch(`https://api-dfl-insights.haqdarshak.com/api/upcoming-camps/?${params}`);
        const upcomingData = await upcomingRes.json();
        setUpcomingCamps(upcomingData.UpcomingCamps || 0);

        const mneRes = await fetch(`https://api-dfl-insights.haqdarshak.com/api/mne-camps/?${params}`);
        const mneData = await mneRes.json();
        setMneCamps(mneData.MneCamps || 0);

        const deletedRes = await fetch(`https://api-dfl-insights.haqdarshak.com/api/deleted-camps/?${params}`);
        const deletedData = await deletedRes.json();
        setDeletedCamps(deletedData.DeletedCamps || 0);

        const participantsRes = await fetch(`https://api-dfl-insights.haqdarshak.com/api/total-participants/?${params}`);
        const participantsData = await participantsRes.json();
        setTotalParticipants(participantsData.TotalParticipants || 0);

        const trainersRes = await fetch(`https://api-dfl-insights.haqdarshak.com/api/total-trainers/?${params}`);
        const trainersData = await trainersRes.json();
        setTotalTrainers(trainersData.Trainers || 0);

        const statesRes = await fetch(`https://api-dfl-insights.haqdarshak.com/api/total-state/?${params}`);
        const statesData = await statesRes.json();
        setTotalState(statesData.TotalState || 0);

        const caseStatusRes = await fetch(`https://api-dfl-insights.haqdarshak.com/api/overall-case-data/?${params}`);
        const caseStatusData = await caseStatusRes.json();
        setOpenCount(caseStatusData.openCount || 0);
        setDocumentSubmitCount(caseStatusData.documentSubmittedCount || 0);
        setBrCount(caseStatusData.brCount || 0);
        setCaseRejectedCount(caseStatusData.caseRejectedCount || 0);
        setProofRejectedCount(caseStatusData.proofRejectedCount || 0);
        setDataCompletedCount(caseStatusData.dataCompleteCount || 0);

        const genderRes = await fetch(`https://api-dfl-insights.haqdarshak.com/api/get-gender/?${params}`);
        const genderData = await genderRes.json();
        setMaleCitizen(genderData.MaleCitizens || 0);
        setFemaleCitizen(genderData.FemaleCitizens || 0);
        setOtherCitizen(genderData.Others || 0);

        setError(null);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (pid) {
      fetchAllData();
    }
  }, [pid, projectName, appliedFilters]);

  const handleApplyFilters = () => {
    setAppliedFilters({
      selectedState: selectedState,
      selectedDistrict: selectedDistrict,
      selectedCampType: selectedCampType,
      selectedCampStatus: selectedCampStatus,
      startDate: startDate,
      endDate: endDate
    });
  };

  const handleClearFilters = () => {
    setSelectedState(null);
    setSelectedDistrict(null);
    setSelectedCampType(null);
    setSelectedCampStatus(null);
    setStartDate('');
    setEndDate('');
    setAppliedFilters({
      selectedState: null,
      selectedDistrict: null,
      selectedCampType: null,
      selectedCampStatus: null,
      startDate: '',
      endDate: ''
    });
  };

  const handleExport = () => {
    const exportData = [
      { 'Category': 'Total Camps', 'Value': totalCamps },
      { 'Category': 'Completed Camps', 'Value': completedCamps },
      { 'Category': 'Ongoing Camps', 'Value': ongoingCamps },
      { 'Category': 'Upcoming Camps', 'Value': upcomingCamps },
      { 'Category': 'Rework from M&E', 'Value': mneCamps },
      { 'Category': 'Deleted Camps', 'Value': deletedCamps },
      { 'Category': 'Total Participants', 'Value': totalParticipants },
      { 'Category': 'Total Trainers', 'Value': totalTrainers },
      { 'Category': 'Total States', 'Value': totalState },
      { 'Category': 'Applications Opened', 'Value': openCount + dataCompletedCount },
      { 'Category': 'Applications Submitted', 'Value': documentSubmitCount },
      { 'Category': 'Applications with Benefit Received', 'Value': brCount },
      { 'Category': 'Applications Aborted', 'Value': caseRejectedCount },
      { 'Category': 'Applications Rejected', 'Value': proofRejectedCount }
    ];

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'DFL_Camp_Summary');
    XLSX.writeFile(workbook, `DFL_Camp_Summary_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Calculate totals
  const totalCampDistribution = completedCamps + ongoingCamps + upcomingCamps + deletedCamps + mneCamps;
  const totalCaseStatus = openCount + documentSubmitCount + brCount + dataCompletedCount + caseRejectedCount + proofRejectedCount;
  const totalGender = maleCitizen + femaleCitizen + otherCitizen;

  const campDistributionData = [
    { status: 'Completed', count: completedCamps, percentage: totalCampDistribution > 0 ? ((completedCamps / totalCampDistribution) * 100).toFixed(1) : 0 },
    { status: 'Ongoing', count: ongoingCamps, percentage: totalCampDistribution > 0 ? ((ongoingCamps / totalCampDistribution) * 100).toFixed(1) : 0 },
    { status: 'Upcoming', count: upcomingCamps, percentage: totalCampDistribution > 0 ? ((upcomingCamps / totalCampDistribution) * 100).toFixed(1) : 0 },
    { status: 'Rework from M&E', count: mneCamps, percentage: totalCampDistribution > 0 ? ((mneCamps / totalCampDistribution) * 100).toFixed(1) : 0 },
    { status: 'Deleted', count: deletedCamps, percentage: totalCampDistribution > 0 ? ((deletedCamps / totalCampDistribution) * 100).toFixed(1) : 0 }
  ];

  const caseStatusData = [
    { status: 'Applications Opened', count: openCount+dataCompletedCount, percentage: totalCaseStatus > 0 ? ((openCount / totalCaseStatus) * 100).toFixed(1) : 0 },
    { status: 'Applications Submitted', count: documentSubmitCount, percentage: totalCaseStatus > 0 ? ((documentSubmitCount / totalCaseStatus) * 100).toFixed(1) : 0 },
    { status: 'Applications with Benefit Received', count: brCount, percentage: totalCaseStatus > 0 ? ((brCount / totalCaseStatus) * 100).toFixed(1) : 0 },
    { status: 'Applications Aborted', count: caseRejectedCount, percentage: totalCaseStatus > 0 ? ((caseRejectedCount / totalCaseStatus) * 100).toFixed(1) : 0 },
    { status: 'Applications Rejected', count: proofRejectedCount, percentage: totalCaseStatus > 0 ? ((proofRejectedCount / totalCaseStatus) * 100).toFixed(1) : 0 }
  ];

  const genderData = [
    { gender: 'Male', count: maleCitizen, percentage: totalGender > 0 ? ((maleCitizen / totalGender) * 100).toFixed(1) : 0 },
    { gender: 'Female', count: femaleCitizen, percentage: totalGender > 0 ? ((femaleCitizen / totalGender) * 100).toFixed(1) : 0 },
    { gender: 'Others', count: otherCitizen, percentage: totalGender > 0 ? ((otherCitizen / totalGender) * 100).toFixed(1) : 0 }
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded max-w-[90vw] ml-[3vw]">
        <strong>Error:</strong> {error}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow border-2 border-gray-300 max-w-[90vw] ml-[4vw] mt-6 p-4 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <h2 className="text-xl font-bold text-pink-900">
            DFL Camps Summary
          </h2>
          <InfoButton title="DFL Camps High Level Summary" />
        </div>
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium text-sm flex items-center gap-2 shadow-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export Excel
        </button>
      </div>

      {/* 4 Cards - White Background with Purple Text */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {/* Total Camps Card */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Camps</p>
              <p className="text-2xl font-bold text-purple-800">{totalCamps}</p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <FaCampground className="text-xl text-purple-600" />
            </div>
          </div>
        </div>

        {/* Total Participants Card */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Participants</p>
              <p className="text-2xl font-bold text-purple-800">{totalParticipants.toLocaleString()}</p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <FaUsers className="text-xl text-purple-600" />
            </div>
          </div>
        </div>

        {/* Total Trainers Card */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Trainers</p>
              <p className="text-2xl font-bold text-purple-800">{totalTrainers}</p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <FaChalkboardTeacher className="text-xl text-purple-600" />
            </div>
          </div>
        </div>

        {/* Total States Card */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total States</p>
              <p className="text-2xl font-bold text-purple-800">{totalState}</p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <FaMapMarkedAlt className="text-xl text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Select
          options={stateOptions.map(state => ({ value: state, label: state }))}
          value={selectedState ? { value: selectedState, label: selectedState } : null}
          onChange={(option) => setSelectedState(option ? option.value : null)}
          placeholder="State"
          isClearable
          isSearchable
          className="text-sm"
        />
        <Select
          options={districtOptions.map(district => ({ value: district, label: district }))}
          value={selectedDistrict ? { value: selectedDistrict, label: selectedDistrict } : null}
          onChange={(option) => setSelectedDistrict(option ? option.value : null)}
          placeholder="District"
          isClearable
          isSearchable
          className="text-sm"
        />
        <Select
          options={campTypeOptions.map(type => ({ value: type, label: type }))}
          value={selectedCampType ? { value: selectedCampType, label: selectedCampType } : null}
          onChange={(option) => setSelectedCampType(option ? option.value : null)}
          placeholder="Camp Type"
          isClearable
          isSearchable
          className="text-sm"
        />
        <Select
          options={campStatusOptions.map(status => ({ value: status, label: status }))}
          value={selectedCampStatus ? { value: selectedCampStatus, label: selectedCampStatus } : null}
          onChange={(option) => setSelectedCampStatus(option ? option.value : null)}
          placeholder="Camp Status"
          isClearable
          isSearchable
          className="text-sm"
        />
      </div>

      {/* Date Range and Action Buttons */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-pink-500 focus:border-pink-500"
            placeholder="Start Date"
          />
          <span className="text-gray-400">to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-pink-500 focus:border-pink-500"
            placeholder="End Date"
          />
        </div>
        <button
          onClick={handleApplyFilters}
          className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium text-sm"
        >
          Apply Filters
        </button>
        <button
          onClick={handleClearFilters}
          className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium text-sm"
        >
          Clear
        </button>
      </div>

      {/* 3 Tables Side by Side - Simple Design */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Camp Distribution Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700">Camp Distribution</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Camp Status</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Number of Camps</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">%</th>
                </tr>
              </thead>
              <tbody>
                {campDistributionData.map((item, index) => (
                  <tr key={index} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm text-gray-700">Camp {item.status}</td>
                    <td className="px-3 py-2 text-sm text-gray-700 text-right">{item.count}</td>
                    <td className="px-3 py-2 text-sm text-gray-700 text-right">{item.percentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Applications Statuses Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700">Applications Status</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Application Status</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Number of Applications</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">%</th>
                </tr>
              </thead>
              <tbody>
                {caseStatusData.map((item, index) => (
                  <tr key={index} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm text-gray-700">{item.status}</td>
                    <td className="px-3 py-2 text-sm text-gray-700 text-right">{item.count}</td>
                    <td className="px-3 py-2 text-sm text-gray-700 text-right">{item.percentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Gender Distribution Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700">Gender Distribution</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Gender</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Citizen Count</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">%</th>
                </tr>
              </thead>
              <tbody>
                {genderData.map((item, index) => (
                  <tr key={index} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm text-gray-700">{item.gender}</td>
                    <td className="px-3 py-2 text-sm text-gray-700 text-right">{item.count}</td>
                    <td className="px-3 py-2 text-sm text-gray-700 text-right">{item.percentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <InfoPopup />

      <style jsx>{`
        @keyframes scaleIn {
          from { 
            opacity: 0; 
            transform: scale(0.95) translateY(-10px); 
          }
          to { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </div>
  );
};

export default DFLProjectSummary;